import { useState, type ReactNode } from 'react';
import { Bold, ChevronsUpDown, Italic, MoreHorizontal, Underline } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/ui/base/alert-dialog';
import { Avatar, AvatarFallback } from '@/ui/base/avatar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/ui/base/breadcrumb';
import { Button } from '@/ui/base/button';
import { Calendar } from '@/ui/base/calendar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/ui/base/collapsible';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/ui/base/command';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/ui/base/drawer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/ui/base/dropdown-menu';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/ui/base/input-otp';
import { Label } from '@/ui/base/label';
import { Skeleton } from '@/ui/base/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/base/table';
import { CardSkeleton, StatCardSkeleton, TableSkeleton } from '@/ui/base/table-skeleton';
import { Textarea } from '@/ui/base/textarea';
import { Toggle } from '@/ui/base/toggle';
import { ToggleGroup, ToggleGroupItem } from '@/ui/base/toggle-group';

import { AmostraSuspensa } from '../AmostraSuspensa';
import { ItemDaDocumentacao } from '../ItemDaDocumentacao';
import { MEDIDAS } from '../medidas';
import { OS_DE_EXEMPLO } from '../exemplos';

function Primitivo({
  id,
  nome,
  arquivo,
  descricao,
  children,
}: {
  id: string;
  nome: string;
  arquivo: string;
  descricao: string;
  children: ReactNode;
}) {
  return (
    <ItemDaDocumentacao id={id} className="min-w-0">
      <div className="flex h-full flex-col rounded-xl border border-border bg-card">
        <div className="space-y-0.5 border-b border-border/50 px-4 py-3">
          <h4 className="text-sm font-semibold text-foreground">{nome}</h4>
          <p className="font-mono text-[11px] text-muted-foreground">ui/base/{arquivo}.tsx</p>
          <p className="text-xs text-muted-foreground">{descricao}</p>
        </div>
        <div className="flex flex-1 flex-wrap items-center gap-3 bg-background/50 p-4">
          {children}
        </div>
      </div>
    </ItemDaDocumentacao>
  );
}

/**
 * Grade "Primitivos (shadcn/ui)": um exemplo mínimo de cada arquivo de
 * `ui/base` que não tem amostra completa acima. Os que não aparecem nem aqui
 * estão em `PRIMITIVOS_SEM_SHOWCASE` (`catalogo.ts`), com o motivo.
 */
export function GradeDePrimitivos() {
  const [aberto, definirAberto] = useState(false);
  const [codigo, definirCodigo] = useState('');
  const [dia, definirDia] = useState<Date | undefined>(() => new Date());

  return (
    <section className="space-y-4" aria-labelledby="primitivos-titulo">
      <div>
        <h3 id="primitivos-titulo" className="text-lg font-semibold">
          Primitivos (shadcn/ui)
        </h3>
        <p className="text-sm text-muted-foreground">
          As peças de <code>ui/base</code> que as amostras acima não mostram, cada uma num exemplo
          mínimo. Telas não as usam soltas quando há componente do sistema para o mesmo fim (ex.:
          confirmação é o <code>ConfirmDialog</code>, que monta o <code>AlertDialog</code>).
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <Primitivo
          id="primitivo-alert-dialog"
          nome="AlertDialog"
          arquivo="alert-dialog"
          descricao="Confirmação que prende o foco até a pessoa decidir; base do ConfirmDialog."
        >
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm">
                Rescindir termo
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Rescindir o termo de exemplo?</AlertDialogTitle>
                <AlertDialogDescription>
                  Amostra da documentação: nenhum dos dois botões grava nada.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction>Rescindir</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </Primitivo>

        <Primitivo
          id="primitivo-avatar"
          nome="Avatar"
          arquivo="avatar"
          descricao="Retrato ou iniciais de pessoa ou entidade; sempre redondo."
        >
          <Avatar>
            <AvatarFallback>PE</AvatarFallback>
          </Avatar>
          <Avatar>
            <AvatarFallback>TG</AvatarFallback>
          </Avatar>
        </Primitivo>

        <Primitivo
          id="primitivo-breadcrumb"
          nome="Breadcrumb"
          arquivo="breadcrumb"
          descricao="Peças da trilha; na casca, quem a monta são as Migalhas (nucleo/navegacao)."
        >
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="#primitivos-titulo">Administração</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Design System</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </Primitivo>

        <Primitivo
          id="primitivo-calendar"
          nome="Calendar"
          arquivo="calendar"
          descricao="Calendário em português (react-day-picker); aparece dentro dos filtros de data."
        >
          <Calendar
            mode="single"
            selected={dia}
            onSelect={definirDia}
            showOutsideDays={false}
            className="rounded-xl border border-border"
          />
          <AmostraSuspensa item="primitivo-calendar" medida={MEDIDAS.diaDeForaDoMes} />
        </Primitivo>

        <Primitivo
          id="primitivo-collapsible"
          nome="Collapsible"
          arquivo="collapsible"
          descricao="Mostra e esconde um trecho; o gatilho diz se está aberto (aria-expanded)."
        >
          <Collapsible open={aberto} onOpenChange={definirAberto} className="w-full space-y-2">
            <CollapsibleTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5">
                <ChevronsUpDown className="h-4 w-4" aria-hidden="true" />
                {aberto ? 'Esconder documentos' : 'Mostrar documentos'}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-1 text-sm text-muted-foreground">
              <p>CRLV do veículo</p>
              <p>Apólice de seguro</p>
            </CollapsibleContent>
          </Collapsible>
        </Primitivo>

        <Primitivo
          id="primitivo-command"
          nome="Command"
          arquivo="command"
          descricao="Lista com busca por digitação; base do SearchableSelect e da BuscaGlobal."
        >
          <Command className="w-full rounded-xl border border-border">
            <CommandInput placeholder="Buscar DRE..." aria-label="Buscar DRE" />
            <CommandList>
              <CommandEmpty>Nenhuma DRE encontrada.</CommandEmpty>
              <CommandGroup heading="DREs">
                <CommandItem>Campo Limpo</CommandItem>
                <CommandItem>Butantã</CommandItem>
                <CommandItem>Ipiranga</CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </Primitivo>

        <Primitivo
          id="primitivo-drawer"
          nome="Drawer"
          arquivo="drawer"
          descricao="Gaveta que sobe do pé da tela; é o ResponsiveModal no celular."
        >
          <Drawer>
            <DrawerTrigger asChild>
              <Button variant="outline" size="sm">
                Abrir gaveta
              </Button>
            </DrawerTrigger>
            <DrawerContent>
              <DrawerHeader>
                <DrawerTitle>Gaveta de exemplo</DrawerTitle>
                <DrawerDescription>Arraste para baixo ou use Fechar.</DrawerDescription>
              </DrawerHeader>
              <DrawerFooter>
                <DrawerClose asChild>
                  <Button variant="outline">Fechar</Button>
                </DrawerClose>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>
        </Primitivo>

        <Primitivo
          id="primitivo-dropdown-menu"
          nome="DropdownMenu"
          arquivo="dropdown-menu"
          descricao="Menu de ações preso a um botão; navegável por setas e fechado por Esc."
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Mais ações">
                <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel>{OS_DE_EXEMPLO.numero}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>Ver histórico</DropdownMenuItem>
              <DropdownMenuItem>Exportar PDF</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Primitivo>

        <Primitivo
          id="primitivo-input-otp"
          nome="InputOTP"
          arquivo="input-otp"
          descricao="Código de uso único, uma caixa por algarismo (segundo fator, verificação)."
        >
          <InputOTP
            maxLength={6}
            value={codigo}
            onChange={definirCodigo}
            aria-label="Código de verificação"
          >
            <InputOTPGroup>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <InputOTPSlot key={i} index={i} />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </Primitivo>

        <Primitivo
          id="primitivo-skeleton"
          nome="Skeleton"
          arquivo="skeleton"
          descricao="Forma do conteúdo enquanto carrega, para a página não saltar."
        >
          <div className="w-full space-y-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </Primitivo>

        <Primitivo
          id="primitivo-table"
          nome="Table"
          arquivo="table"
          descricao="Tabela sem moldura; listagem de verdade é a DataTable, que a usa por dentro."
        >
          <Table aria-label="Tabela simples de exemplo">
            <TableHeader>
              <TableRow>
                <TableHead>Número</TableHead>
                <TableHead>Placa</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-mono">{OS_DE_EXEMPLO.numero}</TableCell>
                <TableCell className="font-mono">{OS_DE_EXEMPLO.placa}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Primitivo>

        <Primitivo
          id="primitivo-table-skeleton"
          nome="TableSkeleton, CardSkeleton e StatCardSkeleton"
          arquivo="table-skeleton"
          descricao="Esqueletos prontos de tabela, cartão e indicador; o EstadoDaTela escolhe entre eles."
        >
          <div className="w-full space-y-3">
            <TableSkeleton columns={2} rows={2} />
            <div className="grid grid-cols-2 gap-3">
              <StatCardSkeleton />
              <CardSkeleton />
            </div>
          </div>
        </Primitivo>

        <Primitivo
          id="primitivo-textarea"
          nome="Textarea"
          arquivo="textarea"
          descricao="Texto longo (observação, justificativa), sempre com rótulo."
        >
          <div className="w-full space-y-1.5">
            <Label htmlFor="ds-observacao">Observação</Label>
            <Textarea id="ds-observacao" placeholder="Descreva o que aconteceu" />
          </div>
        </Primitivo>

        <Primitivo
          id="primitivo-toggle"
          nome="Toggle"
          arquivo="toggle"
          descricao="Botão de dois estados (aria-pressed); ícone sozinho leva rótulo."
        >
          <Toggle aria-label="Negrito">
            <Bold className="h-4 w-4" aria-hidden="true" />
          </Toggle>
        </Primitivo>

        <Primitivo
          id="primitivo-toggle-group"
          nome="ToggleGroup"
          arquivo="toggle-group"
          descricao="Grupo de alternâncias, de escolha única ou múltipla."
        >
          <ToggleGroup type="multiple" aria-label="Formatação do texto">
            <ToggleGroupItem value="negrito" aria-label="Negrito">
              <Bold className="h-4 w-4" aria-hidden="true" />
            </ToggleGroupItem>
            <ToggleGroupItem value="italico" aria-label="Itálico">
              <Italic className="h-4 w-4" aria-hidden="true" />
            </ToggleGroupItem>
            <ToggleGroupItem value="sublinhado" aria-label="Sublinhado">
              <Underline className="h-4 w-4" aria-hidden="true" />
            </ToggleGroupItem>
          </ToggleGroup>
        </Primitivo>
      </div>
    </section>
  );
}
